import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { createSocketConnection, authenticateSocket } from '../../utils/socketConfig';
import SystemManagement from './SystemManagement';

// Icon Components
const TicketIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
  </svg>
);

const ResolvedIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

const PendingIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
  </svg>
);

const SpeedIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
  </svg>
);

const SettingsIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
  </svg>
);

const DashboardIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
  </svg>
);

const ProgressIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
  </svg>
);

const RefreshIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
  </svg>
);

const PersonIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
  </svg>
);


const API_BASE_URL = import.meta.env.VITE_API_URL;

const COLORS = {
  primary: '#1976d2',
  success: '#2e7d32',
  warning: '#ed6c02',
  error: '#d32f2f',
  info: '#0288d1',
  progress: '#9c27b0',
  secondary: '#651fff',
};

const StatCard = ({ title, value, icon, color, subtitle }) => {
  const getColorClasses = (color) => {
    const colorMap = {
      '#1976d2': 'blue',
      '#2e7d32': 'green', 
      '#ed6c02': 'orange',
      '#d32f2f': 'red',
      '#0288d1': 'sky',
      '#9c27b0': 'purple',
      '#651fff': 'violet'
    };
    return colorMap[color] || 'blue';
  };

  const colorClass = getColorClasses(color);
  
  return (
    <div className={`h-full bg-white rounded-lg sm:rounded-xl shadow-lg border border-${colorClass}-200/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:scale-102 hover:bg-${colorClass}-50/50`}>
      <div className="p-3 sm:p-4">
        {/* Icon Section */}
        <div className={`inline-flex p-2 sm:p-3 mb-3 rounded-lg sm:rounded-xl bg-${colorClass}-100/50 relative overflow-hidden`}>
          <div className="relative z-10">
            {React.cloneElement(icon, { 
              className: `w-5 h-5 sm:w-6 sm:h-6 text-${colorClass}-600`
            })}
          </div>
          <div className={`absolute inset-0 bg-gradient-to-br from-${colorClass}-300/30 to-transparent rounded-lg sm:rounded-xl`}></div>
        </div>

        {/* Title Section */}
        <p className="text-gray-700 font-medium mb-1 uppercase text-xs sm:text-sm tracking-wide font-poppins">
          {title}
        </p>

        {/* Value Section */}
        <h3 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-1 font-poppins text-${colorClass}-600`}>
          {value}
        </h3>

        {/* Subtitle Section */}
        {subtitle && (
          <>
            <div className="my-2 border-t border-gray-200/20"></div>
            <p className="text-gray-600 text-xs sm:text-sm font-medium font-poppins">
              {subtitle}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [view, setView] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalTickets: 0,
    pendingTickets: 0,
    inProgressTickets: 0,
    resolvedTickets: 0,
    ictCoorUsers: 0,
    responseTimeMetrics: {
      averageResponseTime: 0
    }
  });
  const [timeRange, setTimeRange] = useState('7days');
  const [socket, setSocket] = useState(null);
  const appVersion = import.meta.env.VITE_VERSION || '0.0.1 BETA';

  // Create authenticated axios instance
  const createAuthAxios = useCallback(() => {
      const token = localStorage.getItem('token');
    return axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
      });
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await createAuthAxios().get(`/admin/stats?time_range=${timeRange}`);

      if (response.data.success) {
        setStats(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch dashboard statistics');
      }
    } catch (err) {
      console.error('Error fetching dashboard statistics:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch dashboard statistics');
    } finally {
      setLoading(false);
    }
  }, [timeRange, createAuthAxios]);

  // Set up socket connection
  useEffect(() => {
    const newSocket = createSocketConnection();

    // Authenticate socket connection
    authenticateSocket(newSocket);

    // Listen for ticket updates
    newSocket.on('ticketUpdated', (updatedTicket) => {
      console.log('Ticket updated event received:', updatedTicket);
      // Refresh stats when a ticket is updated
      fetchStats();
    });

    // Listen for new tickets
    newSocket.on('newTicket', (newTicket) => {
      console.log('New ticket event received in Dashboard:', newTicket);
      // Refresh stats when a new ticket is created
      fetchStats();
    });

    // Listen for ticket deletions
    newSocket.on('ticketDeleted', (data) => {
      console.log('Ticket deleted event received:', data);
      // Refresh stats when a ticket is deleted
      fetchStats();
    });

    setSocket(newSocket);

    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, [fetchStats]);

  // Fetch initial data and set up regular polling
  useEffect(() => {
    fetchStats();

    // Set up polling every 5 minutes
    const intervalId = setInterval(() => {
      fetchStats();
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [fetchStats]);

  // Handle time range change
  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  // Handle refresh button click
  const handleRefresh = () => {
    fetchStats();
    
    // Use socket to emit refresh event
    if (socket) {
      socket.emit('manualDashboardRefresh', { timeRange });
    }
  };

  const resolutionRate = ((stats.resolvedTickets / (stats.totalTickets || 1)) * 100).toFixed(1);

  const renderDashboard = () => (
    <div className="flex flex-col w-full p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl">
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-between items-stretch sm:items-center mb-4">
        <div className="w-full sm:w-48">
          <label className="block text-sm font-medium text-black mb-1 font-poppins">
            Time Range
          </label>
          <select
            value={timeRange}
            onChange={handleTimeRangeChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-poppins text-black"
          >
            <option value="24hours">Last 24 Hours</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
          </select>
        </div>
        
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center justify-center px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed self-end sm:self-auto"
          title="Refresh Data"
        >
          <RefreshIcon className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 justify-center">
        {/* First Row */}
        <div className="col-span-1">
          <StatCard
            title="Total Tickets"
            value={stats.totalTickets}
            icon={<TicketIcon />}
            color={COLORS.primary}
          />
        </div>
        <div className="col-span-1">
          <StatCard
            title="Pending"
            value={stats.pendingTickets}
            icon={<PendingIcon />}
            color={COLORS.warning}
          />
        </div>
        <div className="col-span-1">
          <StatCard
            title="Progress"
            value={stats.inProgressTickets}
            icon={<ProgressIcon />}
            color={COLORS.progress}
          />
        </div>
        
        {/* Second Row */}
        <div className="col-span-1">
          <StatCard
            title="Resolved"
            value={stats.resolvedTickets}
            icon={<ResolvedIcon />}
            color={COLORS.success}
            subtitle={`${resolutionRate}% Resolution Rate`}
          />
        </div>
        <div className="col-span-1">
          <StatCard
            title="Response Time"
            value={`${stats.responseTimeMetrics.averageResponseTime}h`}
            icon={<SpeedIcon />}
            color={COLORS.info}
            subtitle="Average Response Time"
          />
        </div>
        <div className="col-span-1">
          <StatCard
            title="ICT COOR Users"
            value={stats.ictCoorUsers || 0}
            icon={<PersonIcon />}
            color={COLORS.secondary}
            subtitle="Total ICT Coordinators"
          />
        </div>
      </div>
    </div>
  );

  if (loading && !stats.totalTickets) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen flex flex-col p-2 sm:p-4 md:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-6 bg-white p-3 sm:p-4 rounded-lg shadow-sm">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black font-poppins">
          {view === 'dashboard' ? 'Analytics Dashboard' : 'System Management'}
        </h1>
        
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setView('dashboard')}
            className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium font-poppins transition-all duration-200 ${
              view === 'dashboard' 
                ? 'bg-white text-blue-600 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <DashboardIcon className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Dashboard</span>
          </button>
          <button
            onClick={() => setView('management')}
            className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium font-poppins transition-all duration-200 ${
              view === 'management' 
                ? 'bg-white text-blue-600 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <SettingsIcon className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">System Management</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <p className="text-red-700 font-medium text-sm font-poppins">{error}</p>
          <button
            onClick={handleRefresh}
            className="ml-4 p-1 text-red-600 hover:text-red-800 transition-colors duration-200"
          >
            <RefreshIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {view === 'dashboard' ? renderDashboard() : <SystemManagement />}

      {/* Version display in footer */}
      <div className="w-full text-center mt-auto mb-2 py-2">
        <p className="text-xs sm:text-sm font-poppins text-gray-500 italic">
          version {appVersion}
        </p>
      </div>
    </div>
  );
};

export default Dashboard; 